import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Typography,
} from '@mui/material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';

const toolCards = [
  {
    key: 'project-clone', title: 'Project Clone',
    description: 'Copy an existing project into a fresh test draft under a chosen customer and contact.',
    icon: <ContentCopyOutlinedIcon color="primary" />, action: 'project-clone',
  },
  {
    key: "import-history", title: "Import History",
    description: "Review who imported data, when, and what changed. Export saved results.",
    icon: <FileUploadOutlinedIcon color="primary" />, action: "import-history",
  },
  {
    key: 'import',
    title: 'Import',
    description: 'Import data from external files into Elite Core.',
    icon: <FileUploadOutlinedIcon color="primary" />,
    action: 'import',
  },
  {
    key: 'transfer-website',
    title: 'Transfer a Website',
    description: 'Move an existing website into Elite Core and prepare it for PromoShowcase.',
    icon: <LanguageOutlinedIcon color="primary" />,
    comingSoon: true,
  },
  {
    key: 'transfer-online-store',
    title: 'Transfer an Online Store',
    description: 'Move a supported ecommerce storefront into Elite Core and prepare it for PromoStores.',
    icon: <StorefrontOutlinedIcon color="primary" />,
    action: 'transfer-online-store',
  },
];

const ToolsPanel = ({ canManageTools = false, onOpenImport, onOpenOnlineStoreTransfer, onOpenImportHistory, canViewImportHistory = false, onOpenProjectClone, canCloneProjects = false }) => {
  const handleCardClick = (action) => {
    if (!canManageTools) return;
    if (action === 'import') onOpenImport();
    if (action === 'project-clone') onOpenProjectClone?.();
    if (action === 'import-history') onOpenImportHistory?.();
    if (action === 'transfer-online-store') onOpenOnlineStoreTransfer?.();
  };

  return (
    <Box sx={{ padding: 0, marginLeft: '20px', height: '100%' }}>
      <Box sx={{ mb: 2, pr: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          Tools
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Utilities for importing, migrating, and managing data in your Elite Core instance.
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 2,
          pr: 2,
        }}
      >
        {toolCards.filter(card => (card.key !== "import-history" || canViewImportHistory) && (card.key !== 'project-clone' || canCloneProjects)).map((card) => {
          const clickable = Boolean(card.action && canManageTools);
          const cardProps = clickable ? { onClick: () => handleCardClick(card.action) } : {};
          return (
            <Card
              key={card.key}
              elevation={3}
              {...cardProps}
              sx={{
                cursor: clickable ? 'pointer' : 'default',
                padding: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                border: '1px solid #e0e0e0',
                opacity: card.comingSoon ? 0.78 : canManageTools ? 1 : 0.6,
                '&:hover': clickable ? { boxShadow: 6, backgroundColor: '#f9f9f9' } : undefined,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box sx={{ mt: 0.25, opacity: card.comingSoon ? 0.75 : 1 }}>
                  {card.icon}
                </Box>
                <CardContent sx={{ padding: '0 !important', flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {card.title}
                    </Typography>
                    {card.comingSoon && (
                      <Chip
                        size="small"
                        label="Coming Soon"
                        color="default"
                        sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600 }}
                      />
                    )}
                  </Box>
                  <Typography variant="caption" sx={{ opacity: 0.75 }}>
                    {card.description}
                  </Typography>
                </CardContent>
              </Box>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default ToolsPanel;
